import { FeaturesReady, useFeatureValue } from '@growthbook/growthbook-react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'

import { FbEventName, sendFbEventByName } from '@/legacy-src/analytics/fbPixelData'
import { addGtmEvent } from '@/legacy-src/analytics/gtm'
import BubbleAnimation from '@/legacy-src/components/diagnosis/client/components/BubbleAnimation'
import { normalizeDiagnosisDataV2, parseDiagnosisParams } from '@/legacy-src/components/diagnosis/client/utils'
import Loader from '@/legacy-src/components/Loader'
import { DiagnosisRegistrationVersion } from '@/legacy-src/containers/RegistrationContainer'
import { useApis } from '@/legacy-src/contexts/api-context'
import { useSession } from '@/legacy-src/contexts/session-context'
import { useCanonicalUrl } from '@/legacy-src/hooks/useCanonicalUrl'
import useUser from '@/legacy-src/hooks/useUser'
import ClientActions, { SHOW_THERAPISTS } from '@/legacy-src/redux/ClientRedux'
import DiagnosisActions from '@/legacy-src/redux/DiagnosisRedux'
import { getTranslate } from '@/legacy-src/utilities/i18n'
import Toaster from '@/legacy-src/utilities/toaster'

export const MIN_FREE_SLOTS_FALLBACK = 2

const LoadingTherapistsContent = () => {
  const dispatch = useDispatch()
  const { t } = getTranslate()
  const { isLoggedIn } = useSession()
  const router = useRouter()
  const { api } = useApis()
  const { user } = useUser()
  const minFreeSlots = useFeatureValue('min-free-slots-matching-test', MIN_FREE_SLOTS_FALLBACK)
  const diagnosisRegistrationVersion = useFeatureValue(
    'diagnosis-registration-type',
    DiagnosisRegistrationVersion.ORIGINAL,
  )

  const canonicalUrl = useCanonicalUrl()
  const dataToSend = parseDiagnosisParams(router.query.params as string)

  useEffect(() => {
    if (!dataToSend) {
      Toaster.error('errors.defaultError')
      router.push('/')
    }
  }, [dataToSend])

  useEffect(() => {
    const handleDiagnosis = async () => {
      const normalizedData = normalizeDiagnosisDataV2(dataToSend)
      dispatch(ClientActions.onChangeClientValue('showTherapists', SHOW_THERAPISTS.ALL))

      await api.saveDiagnosis({
        goal: 0,
        ...normalizedData,
        minFreeSlots,
      })

      router.push({
        pathname: `/client/recommended-therapist`,
        query: {
          diagnose: true,
          service: dataToSend.service,
        },
      })
    }

    const handler = setTimeout(() => {
      const userInfo = { ...user, email: user?.email || undefined, isLogged: !!user?.id }
      addGtmEvent('diagnoseFinished', '', { ...userInfo })
      sendFbEventByName({ eventName: FbEventName.DiagnoseFinishTotal, user: userInfo })

      if (isLoggedIn) {
        handleDiagnosis()
      } else {
        // set values to redux in order working old parts of diagnosis
        dispatch(
          DiagnosisActions.onChangeDiagnosisValue('shortDiagnosisV2', {
            lead: dataToSend.lead,
            emotions: dataToSend.emotions,
            past: dataToSend.past,
            personality: dataToSend.personality,
          }),
        )
        dispatch(DiagnosisActions.onChangeDiagnosisValue('specialization', dataToSend.specialization))
        dispatch(DiagnosisActions.onChangeDiagnosisValue('service', dataToSend.service))
        dispatch(DiagnosisActions.onChangeDiagnosisValue('sex', parseInt(dataToSend.sex, 10)))
        dispatch(DiagnosisActions.onChangeDiagnosisValue('therapyExperience', dataToSend.therapyExperience))
        dispatch(DiagnosisActions.onChangeDiagnosisValue('language', dataToSend.language))
        dispatch(DiagnosisActions.onChangeDiagnosisValue('therapyActivities', dataToSend.therapyActivities))
        if (dataToSend.therapyFrequency) {
          dispatch(DiagnosisActions.onChangeDiagnosisValue('therapyFrequency', dataToSend.therapyFrequency))
        }
        dispatch(
          DiagnosisActions.onChangeDiagnosisValue('therapistAgeGroup', parseInt(dataToSend.therapistAgeGroup, 10)),
        )
        dispatch(DiagnosisActions.onChangeDiagnosisValue('insuranceId', dataToSend.insuranceId))
        dispatch(DiagnosisActions.onChangeDiagnosisValue('insuranceMatchRequired', dataToSend.insuranceMatchRequired))
        dispatch(DiagnosisActions.onChangeDiagnosisValue('maxPrice', parseInt(dataToSend.maxPrice, 10)))
        dispatch(DiagnosisActions.onChangeDiagnosisValue('partOfDay', dataToSend.partOfDay))
        dispatch(DiagnosisActions.onChangeDiagnosisValue('name', dataToSend.name))
        dispatch(DiagnosisActions.onChangeDiagnosisValue('sessionType', dataToSend.sessionType))
        dispatch(DiagnosisActions.onChangeDiagnosisValue('sessionLocation', dataToSend.sessionLocation))
        dispatch(DiagnosisActions.onChangeDiagnosisValue('diagnosisFinished', true))
        router.push({
          pathname: `/register/diagnosis`,
          query: {
            ...router.query,
            registrationVersion: diagnosisRegistrationVersion,
            redirectUrl: `/client/recommended-therapist`,
          }, // works only for login
        })
      }
    }, 3000)

    return () => {
      clearTimeout(handler)
    }
  }, [isLoggedIn])

  return (
    <>
      <Head>
        <title>{t('headers.diagnosis')}</title>
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      <div className="container">
        <div className="flex h-full min-h-screen flex-grow">
          <BubbleAnimation />
        </div>
      </div>
    </>
  )
}

const LoadingTherapistsPage = (props) => {
  return (
    <FeaturesReady timeout={5 * 1000} fallback={<Loader visible loaderType="section-large" />}>
      <LoadingTherapistsContent {...props} />
    </FeaturesReady>
  )
}

export default LoadingTherapistsPage
